import { computed, onActivated, onScopeDispose, shallowRef, watch } from 'vue'
import type { ComputedRef, VNodeChild } from 'vue'
import type { DataTableBaseColumn, DataTableColumns } from 'naive-ui'
import { useAuthStore } from '@/stores/auth'
import { getPreference, removePreference, setPreference } from '@/composables/usePreference'
import { env } from '@/utils/env'

export interface TableColumnPreference {
  version: 1
  hidden: string[]
  order: string[]
}

export interface TableColumnOption {
  id: string
  group: string
  depth: number
  locked: boolean
  heading: boolean
  renderTitle: () => VNodeChild
}

type Column<Row> = DataTableBaseColumn<Row> & { children?: DataTableColumns<Row> }
const PREFERENCE_EVENT = 'table-columns:changed'

/** 生成列 column 的稳定标识；无键的分组使用标题，无标题列以默认位置 index 兜底。 */
function columnKey<Row>(column: Column<Row>, index: number) {
  const special = 'type' in column ? column.type : undefined
  return (
    column.key ??
    (special ? `$${special}` : `$group:${typeof column.title === 'string' ? column.title : index}`)
  )
}

/** 根据原始列 columns 和合计 summary 保护参与合并的列，避免隐藏或排序后合计错位。 */
export function summaryColumnKeys<Row>(columns: DataTableColumns<Row>, summary: unknown) {
  const keys: Array<string | number> = []
  const locked = new Set<string | number>()
  /** 按叶子列顺序收集 items 的键，与合计单元格的跨度对齐。 */
  function visit(items: DataTableColumns<Row>) {
    for (const item of items) {
      const column = item as Column<Row>
      if (column.children) visit(column.children)
      else keys.push(column.key)
    }
  }
  visit(columns)
  for (const row of Array.isArray(summary) ? summary : [summary]) {
    if (!row || typeof row !== 'object') continue
    keys.forEach((key, index) => {
      const span = row[key]?.colSpan
      if (span === 0) locked.add(key)
      if (typeof span === 'number' && span > 1)
        keys.slice(index, index + span).forEach((value) => locked.add(value))
    })
  }
  return locked
}

/** 读取列宽 value；仅接受像素数值，不猜测百分比和内容自适应宽度。 */
function pixelWidth(value: unknown): number | undefined {
  if (typeof value === 'number' && value > 0) return value
  if (typeof value === 'string' && /^\d+(?:\.\d+)?(?:px)?$/.test(value)) return parseFloat(value)
  return undefined
}

/** 验证本机偏好 value 的版本和字段，损坏或过大的配置回退到默认。 */
function readPreference(value: unknown): TableColumnPreference {
  const empty: TableColumnPreference = { version: 1, hidden: [], order: [] }
  if (!value || typeof value !== 'object') return empty
  const candidate = value as Partial<TableColumnPreference>
  if (
    candidate.version !== 1 ||
    !Array.isArray(candidate.hidden) ||
    !Array.isArray(candidate.order)
  )
    return empty
  if (candidate.hidden.length > 1000 || candidate.order.length > 1000) return empty
  return {
    version: 1,
    hidden: [...new Set(candidate.hidden.filter((id): id is string => typeof id === 'string'))],
    order: [...new Set(candidate.order.filter((id): id is string => typeof id === 'string'))],
  }
}

/** 管理原始列 source 的显示偏好；tableKey 标识逻辑表，lockedKeys 保护合计或业务必要列。 */
export function useTableColumns<Row>(
  source: ComputedRef<DataTableColumns<Row>>,
  tableKey: () => string | undefined,
  lockedKeys: () => Set<string | number> = () => new Set(),
) {
  const auth = useAuthStore()
  const preference = shallowRef<TableColumnPreference>(readPreference(null))
  const storageKey = computed(() => {
    const user = auth.userInfo
    if (!tableKey() || !user?.id || !auth.companyId) return ''
    const profile = auth.endpointProfileInfo
    return `table-columns:${JSON.stringify([
      env.MODE,
      profile?.valid ? profile.apiBase : env.API_BASE_URL,
      auth.privateCompanyCode,
      auth.companyId,
      user.id,
      tableKey(),
    ])}`
  })

  /** 在账号、公司或表格切换及 keep-alive 恢复时重新读取对应偏好。 */
  function restore() {
    preference.value = readPreference(storageKey.value ? getPreference(storageKey.value) : null)
  }
  watch([storageKey, tableKey], restore, { immediate: true })
  onActivated(restore)

  /** 处理其他窗口的偏好变更事件 event，只同步当前表格所属的存储键。 */
  function handleStorage(event: StorageEvent) {
    if (storageKey.value && (event.key === null || event.key === `pref:${storageKey.value}`))
      restore()
  }
  /** 同步同一窗口内相同别名表格的配置变更 event。 */
  function handleLocalChange(event: Event) {
    const change = (event as CustomEvent<{ key: string; preference: TableColumnPreference }>).detail
    if (change.key === storageKey.value) preference.value = readPreference(change.preference)
  }
  window.addEventListener('storage', handleStorage)
  window.addEventListener(PREFERENCE_EVENT, handleLocalChange)
  onScopeDispose(() => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(PREFERENCE_EVENT, handleLocalChange)
  })

  const model = computed(() => {
    const options: TableColumnOption[] = []
    const protectedKeys = lockedKeys()

    /** 遍历同组 columns，path 保持分组身份；必要列把可排序范围分成独立区段。 */
    function visit(columns: DataTableColumns<Row>, path: string[] = []) {
      let segment = 0
      for (const [index, original] of columns.entries()) {
        const column = original as Column<Row>
        const special = 'type' in original ? original.type : undefined
        const key = columnKey(column, index)
        const id = JSON.stringify([...path, `${typeof key}:${key}`])
        const heading = Array.isArray(column.children)
        const locked = Boolean(
          special ||
          column.fixed ||
          ['action', 'actions'].includes(String(key)) ||
          protectedKeys.has(key) ||
          column.key == null,
        )
        if (locked || heading) segment++
        options.push({
          id,
          group: JSON.stringify([path, segment]),
          depth: path.length,
          locked: locked || heading,
          heading,
          renderTitle: () =>
            typeof column.title === 'function'
              ? (column.title as (value: Column<Row>) => VNodeChild)(column)
              : column.title ||
                (special === 'selection' ? '勾选' : special === 'expand' ? '展开' : String(key)),
        })
        if (heading) visit(column.children!, [...path, `${typeof key}:${key}`])
        if (locked || heading) segment++
      }
    }
    visit(source.value)
    return { options }
  })

  const enabled = computed(
    () => Boolean(tableKey()) && model.value.options.some((item) => !item.locked),
  )
  const options = computed(() => model.value.options)
  const resolved = computed(() => {
    if (!enabled.value) return source.value
    const hidden = new Set(preference.value.hidden)
    const rank = new Map(preference.value.order.map((id, index) => [id, index]))
    const optionMap = new Map(options.value.map((item) => [item.id, item]))
    const visibleData = options.value.filter(
      (item) => !item.heading && !item.locked && !hidden.has(item.id),
    )
    if (!visibleData.length) {
      const first = options.value.find((item) => !item.heading && !item.locked)
      if (first) hidden.delete(first.id)
    }

    /** 只重排同一分组内的普通列；children 递归处理，原列及其回调保持不变。 */
    function resolve(columns: DataTableColumns<Row>, path: string[] = []): DataTableColumns<Row> {
      const entries = columns
        .map((original, index) => {
          const column = original as Column<Row>
          const key = columnKey(column, index)
          const id = JSON.stringify([...path, `${typeof key}:${key}`])
          const option = optionMap.get(id)!
          const children = column.children
            ? resolve(column.children, [...path, `${typeof key}:${key}`])
            : undefined
          return {
            original: children
              ? ({ ...original, children } as DataTableColumns<Row>[number])
              : original,
            option,
            index,
            children,
          }
        })
        .filter(({ option, children }) =>
          children ? children.length : option.locked || !hidden.has(option.id),
        )
      entries.sort((a, b) =>
        a.option.group === b.option.group && !a.option.locked && !b.option.locked
          ? (rank.get(a.option.id) ?? 1000 + a.index) - (rank.get(b.option.id) ?? 1000 + b.index)
          : a.index - b.index,
      )
      return entries.map((entry) => entry.original)
    }
    return resolve(source.value)
  })

  const width = computed(() => {
    if (!enabled.value) return undefined
    let total = 0
    let measurable = true
    /** 汇总当前可见叶子列 columns 的宽度，隐藏列不再占据横向滚动空间。 */
    function sum(columns: DataTableColumns<Row>) {
      for (const original of columns) {
        const column = original as Column<Row>
        if (column.children) sum(column.children)
        else {
          const value =
            pixelWidth(column.width) ??
            pixelWidth(column.minWidth) ??
            ('type' in original ? 48 : undefined)
          if (value === undefined) measurable = false
          else total += value
        }
      }
    }
    sum(resolved.value)
    return measurable ? total : undefined
  })

  /** 应用面板提交的 next；仅保存列标识，并保留暂时无权限或未出现的动态列偏好。 */
  function apply(next: TableColumnPreference) {
    const currentIds = new Set(options.value.map((item) => item.id))
    const editable = new Set(options.value.filter((item) => !item.locked).map((item) => item.id))
    const parsed = readPreference(next)
    preference.value = {
      version: 1,
      hidden: [
        ...preference.value.hidden.filter((id) => !currentIds.has(id)),
        ...parsed.hidden.filter((id) => editable.has(id)),
      ],
      order: [
        ...parsed.order.filter((id) => currentIds.has(id)),
        ...preference.value.order.filter((id) => !currentIds.has(id)),
      ],
    }
    if (storageKey.value) {
      setPreference(storageKey.value, preference.value)
      window.dispatchEvent(
        new CustomEvent(PREFERENCE_EVENT, {
          detail: { key: storageKey.value, preference: preference.value },
        }),
      )
    }
  }

  /** 清除当前表格的自定义列偏好，恢复页面定义的默认显示与顺序。 */
  function reset() {
    preference.value = readPreference(null)
    if (storageKey.value) {
      try {
        removePreference(storageKey.value)
      } catch {
        // 存储不可用时仍保留当前视图的默认列配置。
      }
      window.dispatchEvent(
        new CustomEvent(PREFERENCE_EVENT, {
          detail: { key: storageKey.value, preference: preference.value },
        }),
      )
    }
  }

  return { enabled, options, columns: resolved, width, preference, storageKey, apply, reset }
}
